import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './TrainingPage.css';

function AddTrainingModal({ onClose, onAdded }) {
  const { t } = useTranslation();
  const [name,    setName]    = useState('');
  const [link,    setLink]    = useState('');
  const [due,     setDue]     = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axiosInstance.get('/org-team/members')
      // /org-team/members also returns pending (not-yet-accepted) invitations —
      // their `id` is a non-numeric token (e.g. "inv-7") that the backend can't
      // assign a training to. Only offer real, active org members here, same
      // filter TeamManagement.js uses to build its "active members" list.
      .then(r => setMembers((r.data || []).filter(m => !m.pending && m.isActive !== false)))
      .catch(() => {});
  }, []);

  const submit = () => {
    if (!name.trim()) return;
    setLoading(true);
    axiosInstance.post('/training', {
      trainingName: name,
      trainingLink: link,
      dueDate: due || null,
      assignedUserId: assignedUserId || null,
    })
      .then(r => { onAdded(r.data); onClose(); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  return (
    <div className="tr-modal-overlay">
      <div className="tr-modal">
        <div className="tr-modal-header">
          <h3>{t('TrainingPage.addTraining')}</h3>
          <button className="tr-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="tr-modal-body">
          <label className="tr-label">{t('TrainingPage.trainingNameLabel')}</label>
          <input className="tr-input" placeholder={t('TrainingPage.trainingNamePlaceholder')}
                 value={name} onChange={e => setName(e.target.value)} />
          <label className="tr-label" style={{ marginTop: 12 }}>{t('TrainingPage.trainingLinkLabel')}</label>
          <input className="tr-input" type="url" placeholder={t('TrainingPage.trainingLinkPlaceholder')}
                 value={link} onChange={e => setLink(e.target.value)} />
          <label className="tr-label" style={{ marginTop: 12 }}>{t('TrainingPage.dueDateLabel')}</label>
          <input className="tr-input" type="date" value={due}
                 min={new Date().toISOString().split('T')[0]}
                 onChange={e => setDue(e.target.value)} />
          <label className="tr-label" style={{ marginTop: 12 }}>{t('TrainingPage.assignToLabel')}</label>
          <select className="tr-input" value={assignedUserId}
                  onChange={e => setAssignedUserId(e.target.value)}>
            <option value="">{t('TrainingPage.assignToUnassigned')}</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
            ))}
          </select>
        </div>
        <div className="tr-modal-footer">
          <button className="tr-btn-cancel" onClick={onClose}>{t('TrainingPage.cancel')}</button>
          <button className="tr-btn-primary" onClick={submit} disabled={loading || !name.trim()}>
            {loading ? <i className="fas fa-spinner fa-spin" /> : t('TrainingPage.addTraining')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TrainingPage() {
  const { t } = useTranslation();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(() => {
    axiosInstance.get('/training')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markComplete = (id) => {
    setCompleting(id);
    axiosInstance.post(`/training/${id}/complete`)
      .then(r => setData(prev => ({
        ...prev,
        completed: prev.completed + 1,
        pending:   prev.pending - 1,
        items: prev.items.map(i => i.id === id ? r.data : i),
      })))
      .catch(() => {})
      .finally(() => setCompleting(null));
  };

  const deleteTraining = (id) => {
    axiosInstance.delete(`/training/${id}`)
      .then(() => {
        setData(prev => {
          const item = prev.items.find(i => i.id === id);
          return {
            ...prev,
            total:     prev.total - 1,
            completed: item?.completed ? prev.completed - 1 : prev.completed,
            pending:   item?.completed ? prev.pending       : prev.pending - 1,
            items: prev.items.filter(i => i.id !== id),
          };
        });
      })
      .catch(() => {});
  };

  const items = data?.items || [];
  const completePct = data?.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div className="tr-page">
      {showAdd && (
        <AddTrainingModal
          onClose={() => setShowAdd(false)}
          onAdded={item => {
            setData(prev => ({ ...prev, total: prev.total + 1, pending: prev.pending + 1, items: [item, ...prev.items] }));
            setShowAdd(false);
          }}
        />
      )}

      <div className="tr-header">
        <div>
          <h2><i className="fas fa-graduation-cap" style={{ marginRight: 10, color: '#1d4ed8' }} />
            {t('TrainingPage.pageTitle')}
          </h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {t('TrainingPage.pageSubtitle')}
          </div>
        </div>
        <button className="tr-add-btn" onClick={() => setShowAdd(true)}>
          <i className="fas fa-plus" /> {t('TrainingPage.addTraining')}
        </button>
      </div>

      {/* Stats */}
      {data && (
        <div className="tr-stats">
          <div className="tr-stat-card">
            <div className="tr-stat-val">{data.total}</div>
            <div className="tr-stat-lbl">{t('TrainingPage.statTotal')}</div>
          </div>
          <div className="tr-stat-card tr-stat-green">
            <div className="tr-stat-val">{data.completed}</div>
            <div className="tr-stat-lbl">{t('TrainingPage.statCompleted')}</div>
          </div>
          <div className="tr-stat-card tr-stat-amber">
            <div className="tr-stat-val">{data.pending}</div>
            <div className="tr-stat-lbl">{t('TrainingPage.statPending')}</div>
          </div>
          <div className="tr-stat-card tr-stat-pct">
            <div className="tr-stat-val">{completePct}%</div>
            <div className="tr-stat-lbl">{t('TrainingPage.statCompletionRate')}</div>
            <div className="tr-prog-wrap">
              <div className="tr-prog-bar" style={{ width: `${completePct}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="tr-loading"><i className="fas fa-spinner fa-spin" /> {t('TrainingPage.loading')}</div>
      ) : items.length === 0 ? (
        <div className="tr-empty">
          <i className="fas fa-graduation-cap" />
          <div>{t('TrainingPage.emptyTitle')}</div>
          <div style={{ fontSize: 13 }}>{t('TrainingPage.emptyHint')}</div>
        </div>
      ) : (
        <div className="tr-table-wrap">
          <table className="tr-table">
            <thead>
              <tr>
                <th>{t('TrainingPage.colTraining')}</th>
                <th>{t('TrainingPage.colAssignedBy')}</th>
                <th>{t('TrainingPage.colAssignedTo')}</th>
                <th>{t('TrainingPage.colDueDate')}</th>
                <th>{t('TrainingPage.colStatus')}</th>
                <th>{t('TrainingPage.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className={item.completed ? 'tr-row-done' : ''}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.trainingName}</div>
                    {item.trainingLink && (
                      <a href={item.trainingLink} target="_blank" rel="noopener noreferrer"
                         className="tr-link">
                        <i className="fas fa-external-link-alt" /> {t('TrainingPage.openTraining')}
                      </a>
                    )}
                  </td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>{item.assignedBy || '—'}</td>
                  <td style={{ fontSize: 13 }}>
                    {item.assignedUserName || t('TrainingPage.assignToEveryone')}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {item.dueDate ? new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td>
                    {item.completed ? (
                      <span className="tr-badge tr-badge-done">
                        <i className="fas fa-check-circle" /> {t('TrainingPage.statusComplete')}
                      </span>
                    ) : (
                      <span className="tr-badge tr-badge-pending">
                        <i className="fas fa-clock" /> {t('TrainingPage.statusPending')}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {!item.completed && (
                        <button className="tr-action-btn tr-btn-complete"
                                disabled={completing === item.id}
                                onClick={() => markComplete(item.id)}>
                          {completing === item.id
                            ? <i className="fas fa-spinner fa-spin" />
                            : <><i className="fas fa-check" /> {t('TrainingPage.markComplete')}</>}
                        </button>
                      )}
                      <button className="tr-action-btn tr-btn-delete"
                              onClick={() => deleteTraining(item.id)}>
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
